import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink } from "react-router-dom";
import "./Navbar.css";
import { useTranslation } from "react-i18next";
import "../i18n";

const DropdownMenu = ({ items, onClose }) => (
  <div className="dropdown-menu">
    <div className="dropdown-bridge"></div>
    {items.map((item) => (
      <NavLink key={item.to} to={item.to}
        className={({ isActive }) => `dropdown-item ${isActive ? "dropdown-item--active" : ""}`}
        onClick={onClose}>
        <span className="dropdown-item-icon"><i className={item.icon}></i></span>
        <span className="dropdown-item-text">
          <span className="dropdown-item-label">{item.label}</span>
          <span className="dropdown-item-desc">{item.desc}</span>
        </span>
        <i className="fa-solid fa-arrow-right dropdown-item-arrow"></i>
      </NavLink>
    ))}
  </div>
);

const Navbar = () => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(null);
  const navRef = useRef(null);
  const closeTimer = useRef(null);

  const NAV_ITEMS = [
    { label: t("nav.overview"), to: "/", exact: true },
    {
      label: t("nav.solutions"),
      dropdown: [
        { label: t("nav.ourServices"), to: "/services",  icon: "fa-solid fa-layer-group", desc: t("nav.ourServicesDesc") },
        { label: t("nav.techStack"),   to: "/tech-stack", icon: "fa-solid fa-microchip",   desc: t("nav.techStackDesc") },
        { label: t("nav.aboutUs"),     to: "/about",      icon: "fa-solid fa-building",    desc: t("nav.aboutUsDesc") },
      ],
    },
    { label: t("nav.projects"), to: "/portfolio" },
    {
      label: t("nav.company"),
      dropdown: [
        { label: t("nav.team"),         to: "/team",         icon: "fa-solid fa-users",       desc: t("nav.teamDesc") },
        { label: t("nav.testimonials"), to: "/testimonials", icon: "fa-solid fa-star",        desc: t("nav.testimonialsDesc") },
        { label: t("nav.contactUs"),    to: "/contact",      icon: "fa-solid fa-paper-plane", desc: t("nav.contactUsDesc") },
      ],
    },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 768) { setMenuOpen(false); setMobileOpen(null); } };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { return () => { if (closeTimer.current) clearTimeout(closeTimer.current); }; }, []);

  const closeAll = () => { setMenuOpen(false); setOpenDropdown(null); setMobileOpen(null); };
  const handleMouseEnter = useCallback((label) => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpenDropdown(label); }, []);
  const handleMouseLeave = useCallback(() => { closeTimer.current = setTimeout(() => setOpenDropdown(null), 120); }, []);
  const handleDropdownEnter = useCallback(() => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
  const toggleDropdown = (label) => setOpenDropdown((prev) => (prev === label ? null : label));
  const toggleMobileAccordion = (label) => setMobileOpen((prev) => (prev === label ? null : label));

  return (
    <>
      {/* ── Desktop Navbar ── */}
      <nav ref={navRef} className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
        <NavLink to="/" className="logo" onClick={closeAll}>
          <img src={`${process.env.PUBLIC_URL}/SolviotechLogo.png`} alt="Solviotech Logo" className="logo-img" />
        </NavLink>

        <div className="nav-right">
          <ul className="nav-links">
            {NAV_ITEMS.map((item) => (
              <li key={item.label} className={`nav-item ${item.dropdown ? "nav-item--dropdown" : ""}`}
                onMouseEnter={() => item.dropdown && handleMouseEnter(item.label)}
                onMouseLeave={() => item.dropdown && handleMouseLeave()}>
                {item.dropdown ? (
                  <>
                    <button className={`nav-dropdown-trigger ${openDropdown === item.label ? "nav-dropdown-trigger--open" : ""}`}
                      onClick={() => toggleDropdown(item.label)} aria-expanded={openDropdown === item.label}>
                      {item.label}<i className="fa-solid fa-chevron-down nav-chevron"></i>
                    </button>
                    {openDropdown === item.label && (
                      <div className="dropdown-wrapper" onMouseEnter={handleDropdownEnter} onMouseLeave={handleMouseLeave}>
                        <DropdownMenu items={item.dropdown} onClose={closeAll} />
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink to={item.to} end={item.exact} className={({ isActive }) => (isActive ? "active" : "")} onClick={closeAll}>
                    {item.label}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
          <NavLink to="/contact" className="cta-btn" onClick={closeAll}>
            <i className="fa-solid fa-bolt"></i>{t("nav.letsTalk")}
          </NavLink>
        </div>

        <button className={`hamburger ${menuOpen ? "hamburger--open" : ""}`}
          onClick={() => setMenuOpen((v) => !v)} aria-label="Toggle navigation menu" aria-expanded={menuOpen}>
          <span></span><span></span><span></span>
        </button>
      </nav>

      {/* ── Mobile Menu — outside nav so z-index works correctly ── */}
      <div className={`mobile-menu ${menuOpen ? "mobile-menu--open" : ""} ${scrolled ? "mobile-menu--scrolled" : ""}`}>
        <ul className="mobile-nav-links">
          {NAV_ITEMS.map((item) => (
            <li key={item.label} className="mobile-nav-item">
              {item.dropdown ? (
                <>
                  <button className={`mobile-dropdown-trigger ${mobileOpen === item.label ? "mobile-dropdown-trigger--open" : ""}`}
                    onClick={() => toggleMobileAccordion(item.label)}>
                    {item.label}<i className="fa-solid fa-chevron-down mobile-chevron"></i>
                  </button>
                  <div className={`mobile-accordion ${mobileOpen === item.label ? "mobile-accordion--open" : ""}`}>
                    {item.dropdown.map((sub) => (
                      <NavLink key={sub.to} to={sub.to}
                        className={({ isActive }) => `mobile-sub-link ${isActive ? "active" : ""}`}
                        onClick={closeAll}>
                        <i className={sub.icon}></i>{sub.label}
                      </NavLink>
                    ))}
                  </div>
                </>
              ) : (
                <NavLink to={item.to} end={item.exact}
                  className={({ isActive }) => `mobile-link ${isActive ? "active" : ""}`}
                  onClick={closeAll}>
                  {item.label}
                </NavLink>
              )}
            </li>
          ))}
          <li className="mobile-cta-item">
            <NavLink to="/contact" className="mobile-cta-btn" onClick={closeAll}>
              <i className="fa-solid fa-bolt"></i>{t("nav.letsTalk")}
            </NavLink>
          </li>
        </ul>
      </div>

      {/* ── Mobile Overlay ── */}
      {menuOpen && <div className="mobile-overlay" onClick={closeAll}></div>}
    </>
  );
};

export default Navbar;